import { journalBoundaryContext, boundaryValue } from '../boundary-journal.js';
import { patchVitalityCluster } from '../../compile/held-vitality';
import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, handBack, HandBack, insertRoll, itemField, record, requiredInt, upper, definitionNotHeld } from '../context.js';
import { carrierHasTrigger, layerListEntryUuid } from '../statuses/status-trigger-gate.js';
import { executeTriggerDispatch, runTriggerFamily, triggerCall, triggerFamilyPreDraw } from '../trigger-executor.js';
import { effectAttempt } from '../boundary-checkpoint.js';
import { type JanusInput } from '../janus.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { nullifyPostEffectConditions } from '../nullify-post-effect-conditions.js';
import { rollDiceFaces, type DieTuple } from './roll-dice-arr.js';
import { casterLayerProvider } from '../../actions/cast-range-gate.js';
import { ctxProviderReaders, GRANT_EVALUATED } from '../caster-spell-layers.js';
import { createAbilityConditionEvaluator, isAbilityConditionLayer } from '../../compile/ability-conditions.js';
import { answerConditionContext, ConditionContextUnknown } from '../../compile/condition-context.js';
import { C1_CONTRACT as contract } from '../../compile/possessor-compile.js';
import { findInventoryRow } from '../../inventory/flag-adjust.js';
import { upcastRoll } from '../upcast.js';
import { rollTypeModifierStacks } from '../skill-roll.js';
import { type TriggerCandidate, collectTriggerCandidates, gateTriggerCandidate, runTriggerDispatch } from '../../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../../reactions/janus-ctx-trigger-readers.js';
import { statCreature } from '../summon-caster-cantrips.js';
import { effectTargets } from '../is-target-valid-for-effect.js';
import { replaceFormulaClassLevels } from '../../compile/formula-variable-resolution.js';
import { phpJsonTruthy } from '../../compile/php-json-values.js';
import { fullDefinition } from '../../reactions/php-row-rules.js';
import { abilityEntryId } from '../charges/charge-entry.js';
import { consentAllows } from '../trigger-consent.js';
import { cancelPendingStatus, removeStoredStatus, storedStatusHasSpecialTeardown } from '../statuses/status-effect-removal-helpers.js';
import { statusItemId } from '../../statuses/expiry.js';
import { applyStatusEffect } from '../statuses/apply-status-effect.js';
import { AuraOwnerGateUnavailable, isDormantOwnerGatedAura, isHostileAuraSelfAnchor, readStatusCasterId } from '../../compile/creature-status-reader.js';
import type { ItemFieldRow } from '../../compile/item-field-reader.js';
import { skipUnported, unportedSkipRow } from '../../unported.js';

const HEALING_ROLL = '4FB7452A-F130-40C3-83E6-5E92C329D7B2';
const EXTRA_HEALING_ROLL = 'F1CB1CCF-138F-0B16-266F-3AFE1AB8C73D';
const HEALING_REROLL = 'healing_roll_reroll_1s_and_2s';
const PRONE = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const BURNING = '09FC38BC-6060-4B67-A33F-EF7D448F71F1';
const CRIPPLED = '99C862B9-D6AE-4E18-7803-A6F9B42A88E1';
const DYING = '8AE129AF-52D8-CCE8-706A-85767DB52740';
// healing_roll_status_effect_changes_for_target, then remove_recovery_and_dying_states, in legacy order.
const HEAL_BEFORE_BURNING = [DYING, '3BE713AB-37D7-630B-C1F8-9470784BA33E', '22E33A8E-DF5D-2596-8E2D-0E8A8528E47C',
  'BAAA237B-1AA7-0409-5BC7-1A6720573069', 'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1'];
const HEAL_AFTER_BURNING = ['E5452E0D-251E-4BDD-AC25-DADD00A7BEC7', '62E83DEB-4569-AE3A-F671-FF514C508FD2', DYING,
  '553734EC-33D3-4527-AB2E-EF48ACED8DF0', '9AA7588A-4575-9550-343D-6ADB0C74C17F',
  'EE063289-1AF3-41CF-B73C-64A0556E6D07', '2EAAA2E8-1B9C-196D-3E58-C29C09FF8492'];

/** heal_target_arr cleanup, shared by direct healing and regeneration, regardless of the amount restored. */
export function healingStatusChanges(ctx: JanusCtx, input: JanusInput, ability: Row, target: Row): void {
  // The page's lean-status reader returns copies. Recheck persisted identity after a peer removal.
  const stillHeld = (status: Row): boolean => activeStatuses(ctx, target).some(held => held === status
    || !!status.status_id && upper(held.status_id) === upper(status.status_id));
  for (const id of HEAL_BEFORE_BURNING) for (const status of activeStatuses(ctx, target))
    if (statusItemId(status) === id && stillHeld(status)) removeStoredStatus(ctx, target, status, 'healing');
  const burning = activeStatuses(ctx, target).filter(status => statusItemId(status) === BURNING);
  if (burning.length) {
    // Burning's item-wide removal fires the loss listener once, after the row is gone.
    const lossArm = carrierHasTrigger(ctx, target, 'triggers_when_caster_loses_status_effect', 'heal_target_arr:lose-trigger', BURNING);
    const loser = ctx.members.get(ecId(target));
    if (lossArm && !loser) handBack('heal_target_arr:unmodelled:status-lose-trigger');
    const lossEvent = { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: BURNING };
    if (lossArm) triggerFamilyPreDraw(ctx, loser!, lossEvent);
    for (const status of burning) {
      if (!stillHeld(status) || cancelPendingStatus(ctx, target, status)) continue;
      if (status.status_id && !status.tick_added)
        ctx.statusesRemoved.push({ status_id: upper(status.status_id), status_item_id: BURNING, target: ecId(target) });
    }
    const statusBucket = record(target.statuses);
    if (Array.isArray(statusBucket?.target))
      statusBucket.target = statusBucket.target.filter((status: unknown) => upper(record(status)?.status_item_id) !== BURNING);
    if (lossArm) runTriggerFamily(ctx, lossEvent, loser!, null, [loser!], false, undefined, true);
  }
  for (const id of HEAL_AFTER_BURNING) for (const status of activeStatuses(ctx, target))
    if (statusItemId(status) === id && stillHeld(status)) removeStoredStatus(ctx, target, status, 'healing');
  const crippled = activeStatuses(ctx, target).filter(status => statusItemId(status) === CRIPPLED);
  for (const status of crippled) if (stillHeld(status)) removeStoredStatus(ctx, target, status, 'single');
  if (crippled.length) applyStatusEffect(ctx, { ...input, abilityId: PRONE }, ability, PRONE, 'status_effect_applied', target, target,
    { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, true, 0, false, null);
}
/** discern_variable_tags' [<class variable_name>_level] tags (replace_class_level_placeholders), one per playable class. */
const CLASS_LEVEL_TAGS: ReadonlySet<string> = new Set(contract.rollup.classes.map((entry) => `[${entry.variable_name}_level]`));
/** The essence tags discern_variable_tags reads off the healer (layer_value($caster_arr, <essence>)) — temp-v-roll's twin. */
const ESSENCE_TAGS: ReadonlySet<string> = new Set(['[body]', '[mind]', '[soul]']);
// Recipient statuses supply recovery cleanup, payload against modifiers/no-heal blocks, and matching Healing Roll receiver listeners.
// A lingering item's own dice/damage/contest layers are inert unless the shared trigger dispatcher fires that item.
/** A recipient status's held definition (the inline copy when it carries layers, else the catalog row); unheld → the page fetches it. */
function recipientStatusDefinition(ctx: JanusCtx, status: Row): Row {
  const def = heldHealingStatusDefinition(ctx, status);
  if (!def) definitionNotHeld('healing_roll:target-status-definition-not-held', statusItemId(status));
  return def!;
}
/** creature_payload_statuses: keep structural anchors for cleanup, but omit their payload and dormant owner-gated auras. */
function healingPayloadStatuses(ctx: JanusCtx, creature: Row): Row[] {
  const value = (row: ItemFieldRow | null, key: string): unknown => ctx.read.value(row as Row | null, key);
  return activeStatuses(ctx, creature).filter(status => {
    const def = recipientStatusDefinition(ctx, status);
    const target = status.status_target_id ?? status.target ?? ecId(creature);
    const caster = readStatusCasterId(status, target);
    if (isHostileAuraSelfAnchor(def, status.status_aoe_source, caster, target, value)) return false;
    try {
      return !isDormantOwnerGatedAura({ ...status, status_arr: def, status_caster_id: caster }, value, id => {
        const owner = ctx.members.get(upper(id));
        return owner ? { ...owner, encounter_creatures: { ...ec(owner), v_current: ecLean(owner, 'v_current') },
          statuses: { ...record(owner.statuses), target: activeStatuses(ctx, owner) } } : null;
      }, creature);
    } catch (error) {
      if (error instanceof AuraOwnerGateUnavailable) handBack('creature_payload_statuses:aura-owner-state-not-held');
      throw error;
    }
  });
}
/** Use the shared roll-type source walk with the healing recipient's payload statuses, including same-use additions/removals. */
function healingAgainstStacks(ctx: JanusCtx, target: Row, caster: Row, rollType: string): number {
  const statuses = healingPayloadStatuses(ctx, target);
  const definitions = new Map(statuses.map(status => [statusItemId(status), recipientStatusDefinition(ctx, status)]));
  const view: JanusCtx = { ...ctx, statusesAdded: [], read: { ...ctx.read,
    definition: id => definitions.get(upper(id)) ?? ctx.read.definition(id),
    statuses: row => row === target ? statuses : activeStatuses(ctx, row) } };
  return rollTypeModifierStacks(view, target, [rollType], ['against_adv', 'against_dis'],
    { usedItemType: '', usedRollType: rollType, rollTo: ecId(caster), opposing: caster });
}
/** ft_creature_no_heal_block_reason (heal_target_arr.php): the name of the first status forbidding the creature to regain Vitality —
 *  no_healing_rolls on its definition, or a status_effect_permanent entry (a disease → Withering) whose definition carries it — unless the
 *  creature holds one of its if_caster_has_status_effect_not_active exemptions (Wet). '' = healing allowed. */
function healingBlockedBy(ctx: JanusCtx, creature: Row): string {
  const held = new Set(activeStatuses(ctx, creature).map(statusItemId));
  for (const status of healingPayloadStatuses(ctx, creature)) {
    const def = recipientStatusDefinition(ctx, status);
    const forbids = ctx.read.has(def, 'no_healing_rolls') || ctx.read.entries(def, 'status_effect_permanent').some((entry) => {
      const id = layerListEntryUuid(entry);
      return UUID.test(upper(id)) && ctx.read.has(noHealNestedDefinition(ctx, def, id), 'no_healing_rolls');
    });
    if (!forbids) continue;
    if (ctx.read.entries(def, 'if_caster_has_status_effect_not_active').some((entry) => held.has(upper(layerListEntryUuid(entry))))) continue;
    return String(itemField(def, 'item_name') ?? 'a status effect');
  }
  return '';
}
/** ft_status_def_forbids_healing's nested read: the status definition's own catalog copy when it carries layers, else the held row. */
function noHealNestedDefinition(ctx: JanusCtx, def: Row, rawId: unknown): Row {
  const catalog = record(def.catalog), id = upper(rawId);
  const embedded = record(catalog?.[String(rawId)] ?? catalog?.[id]);
  if (embedded && Object.keys(record(embedded.item_layer_types) ?? {}).length) return embedded;
  const held = fullDefinition(ctx.read.definition(id), 'any');
  if (!held) definitionNotHeld('healing_roll:no-heal-status-definition-not-held', id);
  return held!;
}
/** Status modifiers need every held definition; the no-heal walk alone settles nested definitions, stopping at its first blocker. */
function settleRecipientStatuses(ctx: JanusCtx, target: Row): void {
  for (const status of activeStatuses(ctx, target)) recipientStatusDefinition(ctx, status);
  healingBlockedBy(ctx, target);
}

/** heal_target_arr's amount application, shared by rolled healing and damage that heals its caster. */
function healVitalityAmount(ctx: JanusCtx, input: JanusInput, ability: Row, target: Row, amount: number): {
  applied: number; reason: string | null; blockedBy: string;
} {
  if (requiredInt(ecLean(target, 'encounter_creature_dead') ?? ctx.read.deadState(target), 'healing_roll:dead-state-not-held'))
    return { applied: 0, reason: 'dead', blockedBy: '' };
  const vitality = record(record(target.computed)?.vitality);
  // Damage (including self-damage or retaliation) can have changed the instance since its last compile.
  const before = requiredInt(ecLean(target, 'v_current'), 'heal_target_arr:vitality-or-dead');
  const maximum = requiredInt(vitality?.v_max, 'heal_target_arr:vitality-or-dead');
  const effectiveMax = requiredInt(vitality?.effective_v_max ?? maximum, 'heal_target_arr:effective-v-max-not-held');
  const blockedBy = healingBlockedBy(ctx, target);
  const reason = blockedBy ? 'healing blocked' : before >= maximum ? 'already full' : null;
  let applied = 0;
  if (!reason) {
    const healed = Math.max(0, Math.min(effectiveMax, before + Math.trunc(amount)));
    ec(target).v_current = healed;
    patchVitalityCluster(target, healed);
    applied = Math.max(0, healed - before);
  }
  healingStatusChanges(ctx, input, ability, target);
  return { applied, reason, blockedBy };
}

/** Lifesteal is an amount restore after damage, without another Healing Roll or its roll-type listeners. */
export function damageHealsCaster(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, amount: number): void {
  if (amount <= 0) return;
  const healed = healVitalityAmount(ctx, input, ability, caster, amount);
  insertRoll(ctx, { roll_type_1: 'damage_heals_caster', roll_item_id: input.abilityId, by: caster, to: caster,
    roll_result: healed.blockedBy ? 0 : amount,
    rolls_full: healed.blockedBy ? [['∅', `Healing Nullified · ${healed.blockedBy}`]] : [],
    dice: 0, die: 0, mod: 0,
    feedback: { rolled: amount, applied: healed.applied, verb: 'restored', reason: healed.reason } });
}
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

const EXTRA_HEALING_LAYERS: ReadonlySet<string> = new Set([
  'roll_type', 'dice', 'die', 'mod', 'roll_type_adv', 'roll_type_dis',
  HEALING_REROLL,
  'requires_class', 'requires_class_level', 'requires_subclass', 'requires_level', 'unenchantable', 'cost_gp',
]);
/** Extra Healing's requirements describe its grant; runtime conditions/effects need a port before this passive can roll. */
function extraHealingPassive(ctx: JanusCtx, def: Row): boolean {
  if (upper(ctx.read.value(def, 'roll_type')) !== EXTRA_HEALING_ROLL) return false;
  if (itemField(def, 'item_type') !== 'passive_ability') handBack('healing_roll:extra-healing:not-passive');
  for (const key of Object.keys(record(def.item_layer_types) ?? {}))
    if (!EXTRA_HEALING_LAYERS.has(key) && ctx.read.has(def, key)) handBack(`healing_roll:extra-healing:unmodelled:${key}`);
  return true;
}

interface ExtraHealingPool { definition: Row; dice: number; die: number; mod: number }
/** Bucket passives, then directly held passive statuses. The compiled bucket is the reader's PC projection; an active bucket copy
 * masks its status/display mirror, but an inactive copy never masks an active status. */
function extraHealingPools(ctx: JanusCtx, input: JanusInput, caster: Row): ExtraHealingPool[] {
  const pools: ExtraHealingPool[] = [], seen = new Set<string>();
  const add = (def: Row, id: string, status = false): void => {
    if (!extraHealingPassive(ctx, def)) return;
    if (status && seen.has(id)) return;
    seen.add(id);
    const dice = healingVariable(ctx, input, caster, ctx.read.value(def, 'dice'));
    const die = healingVariable(ctx, input, caster, ctx.read.value(def, 'die'));
    const mod = healingVariable(ctx, input, caster, ctx.read.value(def, 'mod'));
    if (dice < 0 || dice > 100 || die < 0 || die > 1000 || (dice > 0 && die < 1)) handBack('healing_roll:extra-healing:dice-shape');
    pools.push({ definition: def, dice, die, mod });
  };
  for (const entry of ctx.read.entries(caster, 'passive_ability')) {
    const passive = record(entry);
    if (passive && 'passive_conditions_active' in passive && !phpJsonTruthy(ctx.read.value(passive, 'passive_conditions_active'))) continue;
    const id = abilityEntryId(entry);
    const def = fullDefinition(record(passive?.item_arr), 'any') ?? fullDefinition(ctx.read.definition(id), 'any');
    if (!def) definitionNotHeld('healing_roll:passive-definition-not-held', id);
    add(def!, id);
  }
  for (const status of activeStatuses(ctx, caster)) {
    const def = casterStatusDefinition(ctx, status);
    if (itemField(def, 'item_type') === 'passive_ability') add(def, upper(status.status_item_id), true);
  }
  return pools;
}

/** Providence uses the shared provider walk; unstamped providers judge runtime gates in the current healing frame. */
function healingRerolls(ctx: JanusCtx, caster: Row): boolean {
  const read = ctxProviderReaders(ctx);
  const gap = (): never => handBack('healing_roll:unmodelled:compiled-healing-passive');
  const active = (entry: unknown): boolean => {
    const row = record(entry);
    return !row || !('passive_conditions_active' in row) || phpJsonTruthy(ctx.read.value(row, 'passive_conditions_active'));
  };
  // The walker matches definitions. Carry an entry's explicit verdict onto a detached definition, including inline grants.
  const stamped = (def: Row, entry: unknown): Row => {
    const row = record(entry);
    return row && 'passive_conditions_active' in row ? { ...def, passive_conditions_active: ctx.read.value(row, 'passive_conditions_active') } : def;
  };
  let conditions: ReturnType<typeof createAbilityConditionEvaluator> | undefined;
  const eligible = (def: Row): boolean => {
    if ('passive_conditions_active' in def) return active(def);
    for (const key of [...Object.keys(def), ...Object.keys(record(def.item_layer_types) ?? {}), ...Object.keys(record(def.layers) ?? {})])
      if (key.startsWith('if_') && !isAbilityConditionLayer(key) && ctx.read.has(def, key)) gap();
    const host = { ...caster, statuses: { ...record(caster.statuses), target: activeStatuses(ctx, caster) } };
    conditions ??= createAbilityConditionEvaluator({ catalogRows: contract.catalogRows, abilityTypes: contract.abilityTypes,
      layerReaders: { layerValue: (row, key) => row ? ctx.read.value(row as Row, key) ?? null : null,
        layerEntries: (row, key) => row ? ctx.read.entries(row as Row, key) : [] },
      aggregationStrategy: name => contract.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
      resolveCarrierRef: id => ctx.read.definition(upper(id)),
      queryTemplateDefinition: ctx.read.definition, resolveInventoryInstance: id => findInventoryRow(host, id) ?? gap(),
      equipmentSlots: contract.equipmentSlots, vitalityStatusIds: contract.vitalityStatusIds, skillEntries: contract.skillEntries,
      conditionSource: (kind, args) => {
        const members = [...ctx.members.values()].map(row => row === caster ? host : row);
        const scene = ctx.read.possessorScene?.(host, members)?.conditionScene?.();
        const answer = kind === 'present-light' && ctx.read.lightTypesPresent ? ctx.read.lightTypesPresent(host, members)
          : answerConditionContext(kind, args, { encounter: scene?.encounter ?? { ...ctx.encounter, encounter_creatures_arr: members },
          campaign: scene?.campaign, value: ctx.read.value, entries: ctx.read.entries, definition: ctx.read.definition,
          abilityTypes: contract.abilityTypes, equipmentSlots: contract.equipmentSlots });
        return answer ?? gap();
      } });
    try { return conditions.check(def, host); }
    catch (error) { if (error instanceof ConditionContextUnknown) gap(); throw error; }
  };
  const provider = casterLayerProvider({ ...read,
    entries: (row, key) => ctx.read.entries(row, key).filter(active).map(entry => {
      const row = record(entry), inline = record(row?.item_arr);
      return row && inline ? { ...row, item_arr: stamped(inline, row) } : entry;
    }),
    statuses: row => activeStatuses(ctx, row).filter(active),
    resolveItem: entry => {
      const def = fullDefinition(read.resolveItem!(entry), 'any');
      if (!def) definitionNotHeld('healing_roll:passive-definition-not-held', abilityEntryId(entry));
      return stamped(def!, entry);
    },
  }, caster, HEALING_REROLL, eligible, GRANT_EVALUATED);
  if (provider === undefined) gap();
  return provider !== null;
}

/** One pass after selection/forced faces: keep unaffected order, then append replacements in original order (legacy's unset + push).
 * Discarded ADV/DIS faces remain display-only; a replacement 1/2 is kept without another reroll. */
function rerollHealingOnesAndTwos(ctx: JanusCtx, rolled: { total: number; faces: DieTuple[] }, die: number): void {
  const faces: DieTuple[] = [], replacements: DieTuple[] = [];
  for (const face of rolled.faces) {
    if (face[2]?.kept !== false && (face[0] === 1 || face[0] === 2)) {
      const replacement = ctx.roll.d(die);
      rolled.total += replacement - face[0];
      replacements.push([replacement, `Reroll HR 1 or 2: ${face[0]}`, { ...face[2], kept: true }]);
    } else faces.push(face);
  }
  rolled.faces = [...faces, ...replacements];
}

/** Shared discern_variable_tags subset for primary modifiers and extra dice/die/mod. A held lean definition's omitted zeros are zero;
 * a missing derived value or unknown tag refuses, including the existing class-level and essence tags. */
function healingVariable(ctx: JanusCtx, input: JanusInput, caster: Row, raw: unknown): number {
  if (raw === '[total_level]') {
    const level = ctx.read.value(statCreature(ctx, caster, input.abilityId), 'total_level');
    if (level == null) handBack('healing_roll:total-level-not-held');
    return requiredInt(level, 'healing_roll:invalid-derived-modifier');
  }
  if (raw === '[spellcasting_essence]') {
    const spellcasting = record(record(record(statCreature(ctx, caster, input.abilityId).computed)?.creature)?.spellcasting);
    if (spellcasting?.essence_mod == null) handBack('healing_roll:spellcasting-essence-not-held');
    return requiredInt(spellcasting.essence_mod, 'healing_roll:invalid-derived-modifier');
  }
  if (raw === '[half_v_max]') {
    const effectiveMax = record(record(caster.computed)?.vitality)?.effective_v_max;
    if (effectiveMax == null) handBack('healing_roll:effective-v-max-not-held');
    return Math.trunc(requiredInt(effectiveMax, 'healing_roll:invalid-derived-modifier') / 2);
  }
  if (typeof raw === 'string' && CLASS_LEVEL_TAGS.has(raw)) {
    const resolved = replaceFormulaClassLevels(statCreature(ctx, caster, input.abilityId), raw, contract.rollup);
    if (!/^-?\d+$/.test(resolved)) handBack('healing_roll:class-level-not-held');
    return requiredInt(resolved, 'healing_roll:invalid-derived-modifier');
  }
  if (typeof raw === 'string' && ESSENCE_TAGS.has(raw)) {
    const essence = ctx.read.value(caster, raw.slice(1, -1));
    if (essence == null) handBack(`healing_roll:${raw.slice(1, -1)}-not-held`);
    return requiredInt(essence, 'healing_roll:invalid-derived-modifier');
  }
  if (raw == null) return 0;
  if (/^-?\d+$/.test(String(raw))) return requiredInt(raw, 'healing_roll:invalid-mod');
  handBack('healing_roll:mod-tag');
}

// ── healing_roll's PRE-ROLL fire_caster_makes_roll_type_trigger (healing_roll.php ~L178) — the healer's triggers_when_caster_makes_roll_type
// [Healing Roll] carriers, IN THE BROWSER (2026-10-07, Thief Corpsman L35 "Critical Care": heals rolled at Maximum for targets at or below
// 1/4 Vitality). A pure roll-modifier PASSIVE — it authors only the trigger, the roll-type extremes / halve / double, its target-status gate
// and its requirements (CMRT_HINT_ONLY_LAYERS, twin of janus_cmrt_passive_hints_for_target.php's FT_CMRT_HINT_ONLY_PASSIVE_LAYERS) — is
// judged against EACH healed target (janus_trigger's passive gates: active stamp, passive_ability_trigger_conditions_met against the inciting
// target, charges, the triggers-table opt-out) and, when it fires, merges its hints (janus_cmrt_merge_post_effect_hints_from_ability_row)
// into that target's roll (resolve_next_roll_type_dice_arr: every die at its max / min face, then halve / double after the modifier). Every
// other carrier runs through the shared executor (healingCmrtExecutorFire below — 2026-10-07 follow-up).
const CMRT_TRIGGER = 'triggers_when_caster_makes_roll_type';
const RECEIVER_TRIGGER = 'triggers_on_roll_type_against_the_caster';
/** Receiver listeners are independent boundaries: preview their dependencies before the heal, then dispatch/skip each due listener. */
function healingReceiverTriggers(ctx: JanusCtx, target: Row, caster: Row, preview = false): void {
  if (ctx.trigger) return;
  ctx = journalBoundaryContext(ctx); target = boundaryValue(ctx, target); caster = boundaryValue(ctx, caster);
  const event = { trigger: RECEIVER_TRIGGER, rollType: HEALING_ROLL };
  triggerCall(() => runTriggerDispatch(janusCtxTriggerReaders(ctx), target, event, caster, (spec, owner) => {
    if (preview) triggerFamilyPreDraw(ctx, owner, { ...event, restrictToAbilityIds: [spec.candidate.abilityId] }, undefined, caster);
    else if (consentAllows(ctx, owner, spec.candidate.ability, spec.candidate.abilityId, event.trigger, spec.candidate.carrier))
      // Restore a partially resolved listener before logging its gap; earlier successful siblings and the incoming heal stay saved.
      effectAttempt(ctx, '', (ctx) => executeTriggerDispatch(ctx, event, spec, owner, [target]), { primary: true });
    return owner;
  }, (candidate, run) => {
    try { triggerCall(run); return true; }
    catch (error) {
      // Definitions and optional consent retain the page's normal fetch/prompt retry.
      if (!(error instanceof HandBack) || /definition-not-held|trigger-consent/.test(error.reason)) throw error;
      if (!preview) {
        const name = String(itemField(record(target.player_characters), 'player_name')
          ?? ecLean(target, 'encounter_creature_name') ?? itemField(target, 'item_name') ?? 'Creature');
        const listener = String(itemField(candidate.ability, 'item_name') ?? 'Healing Roll listener');
        const reason = `${name}: healing-receiver: ${listener} skipped (${error.reason})`
          .replace(/[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}/gi, 'unavailable definition');
        ctx.rows.push({ ...(ctx.dryRun ? unportedSkipRow(reason) : skipUnported(reason)), roll_by: ecId(target) });
      }
      return false;
    }
  }));
}
const CMRT_HINT_ONLY_LAYERS: ReadonlySet<string> = new Set([
  CMRT_TRIGGER,
  'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min', 'roll_type_roll_result_is_halved', 'roll_type_roll_result_is_doubled',
  'next_roll_type_is_rolled_at_max', 'next_roll_type_is_rolled_at_min', 'next_roll_type_roll_result_is_halved', 'next_roll_type_roll_result_is_doubled',
  'if_target_has_status_effect_active', 'if_target_has_status_effect_not_active',
  'requires_class', 'requires_class_level', 'requires_subclass', 'requires_level', 'unenchantable', 'cost_gp',
]);
interface HealHints { max: boolean; min: boolean; halve: boolean; double: boolean }
const listsHealingRoll = (ctx: JanusCtx, def: Row | null, key: string): boolean =>
  ctx.read.entries(def, key).some((entry) => upper(layerListEntryUuid(entry)) === HEALING_ROLL);
/** resolve_target_status_effect_roll_type_against_modifiers (resolve_next_roll_type_dice_arr.php): the healed creature's statuses force a
 *  Healing Roll made against it to its max / min faces and double / halve its result; a target_marked_by_caster status counts only for the
 *  creature that applied it. */
function healingAgainstModifiers(ctx: JanusCtx, caster: Row, target: Row): HealHints {
  const hints: HealHints = { max: false, min: false, halve: false, double: false };
  for (const status of healingPayloadStatuses(ctx, target)) {
    const def = recipientStatusDefinition(ctx, status);
    // A stored row names its applier status_caster_id, a row this dispatch added names it caster (StatusRow); no applier → not the healer.
    if (ctx.read.has(def, 'target_marked_by_caster') && upper(status.status_caster_id ?? status.caster) !== ecId(caster)) continue;
    hints.max ||= listsHealingRoll(ctx, def, 'roll_type_against_is_rolled_at_max');
    hints.min ||= listsHealingRoll(ctx, def, 'roll_type_against_is_rolled_at_min');
    hints.halve ||= listsHealingRoll(ctx, def, 'roll_type_against_roll_result_is_halved');
    hints.double ||= listsHealingRoll(ctx, def, 'roll_type_against_roll_result_is_doubled');
  }
  return hints;
}
function hintOnlyPassive(candidate: TriggerCandidate): boolean {
  if (candidate.pool !== 'carrier' || candidate.carrier !== 'passive_ability') return false;
  if (String(itemField(candidate.ability, 'item_type') ?? '') !== 'passive_ability') return false;
  const keys = Object.keys(record(candidate.ability.item_layer_types) ?? {});
  return keys.length > 0 && keys.every((key) => CMRT_HINT_ONLY_LAYERS.has(key));
}
// ── 2026-10-07 follow-up (Maximum Mend 15F99126, board R-160): every OTHER CMRT [Healing Roll] carrier — a reaction (Maximum Mend: its
// charge, the reaction, its 10-minute item-as-status that its own triggers_on_next_roll_type uses up on THIS roll), a free ability, a passive
// with an effect of its own — fires through the shared executor exactly as janus_trigger() dispatches it (janus() in trigger mode, the RX
// decision twin), once per roll (the first target that lets one dispatch spends the frame). Each DISPATCHED carrier merges its ability-row
// roll-type hints into that target's roll (janus_cmrt_merge_post_effect_hints_from_ability_row). All carriers, including hint-only
// passives, dispatch through the shared executor in carrier order. Later targets only re-judge the hint-only passives.
/** The definitions a dispatched carrier can leave on the healer: its own row (an item-as-status) and the statuses it applies. */
function carrierStatusDefinitions(ctx: JanusCtx, ability: Row): Row[] {
  const out: Row[] = [ability];
  for (const key of ['status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_target_after_cast'])
    for (const entry of ctx.read.entries(ability, key)) {
      const def = ctx.read.definition(upper(layerListEntryUuid(entry)));
      if (!def) definitionNotHeld('healing_roll:cmrt-applied-status-definition-not-held', layerListEntryUuid(entry));
      out.push(def!);
    }
  return out;
}
/** The healer's CMRT carriers, settled before any draw; pure hints are additionally re-judged on later targets. */
function healingCmrtCarriers(ctx: JanusCtx, caster: Row, rollType: string): { hintOnly: TriggerCandidate[]; dispatch: boolean } {
  if (ctx.trigger || rollType === '') return { hintOnly: [], dispatch: false }; // janus_trigger_in_progress() / no roll type: no fire
  if (listsHealingRoll(ctx, caster, CMRT_TRIGGER)) handBack('healing_roll:unmodelled:cmrt-on-row');
  const event = { trigger: CMRT_TRIGGER, rollType: HEALING_ROLL };
  const { carriers, statuses } = triggerCall(() => collectTriggerCandidates(janusCtxTriggerReaders(ctx), caster, event));
  const hintOnly = carriers.filter(hintOnlyPassive);
  const all = [...carriers, ...statuses];
  if (all.length) triggerFamilyPreDraw(ctx, caster, event);
  // A carrier applied as its own status and used up by this roll (Maximum Mend): PHP's removal fires the healer's
  // triggers_when_caster_loses_status_effect for it — not modelled for a status this use applied → PHP (Codex review 2026-10-08).
  for (const candidate of all) for (const def of carrierStatusDefinitions(ctx, candidate.ability))
    if (listsHealingRoll(ctx, def, 'triggers_on_next_roll_type')
        && carrierHasTrigger(ctx, caster, 'triggers_when_caster_loses_status_effect', 'healing_roll:next-roll-status-lose-trigger', upper(itemField(def, 'item_id'))))
      handBack('healing_roll:unmodelled:next-roll-status-lose-trigger');
  return { hintOnly, dispatch: all.length > 0 };
}
/** fire_caster_makes_roll_type_trigger() for an executor-run carrier set, against `target` (the inciting target): dispatched once per roll
 *  (a fire that dispatches nothing leaves the frame open for the next target). Returns the merged hints of what DISPATCHED. */
function healingCmrtExecutorFire(ctx: JanusCtx, caster: Row, target: Row, frame: { fired: boolean }): HealHints {
  const hints: HealHints = { max: false, min: false, halve: false, double: false };
  if (frame.fired) return hints;
  const event = { trigger: CMRT_TRIGGER, rollType: HEALING_ROLL };
  const inciting = ecId(target) && ctx.members.has(ecId(target)) ? target : null;
  // targets_arr = janus_trigger_resolve_self_targets($caster_arr) — the healer; trigger_inciting_target_arr = the healed target
  triggerFamilyPreDraw(ctx, caster, event, undefined, inciting);
  const executed = runTriggerFamily(ctx, event, caster, inciting, [caster], false);
  if (!executed.length) return hints;
  frame.fired = true;
  for (const { ability: def } of executed) {
    hints.max ||= listsHealingRoll(ctx, def, 'roll_type_is_rolled_at_max') || listsHealingRoll(ctx, def, 'next_roll_type_is_rolled_at_max');
    hints.min ||= listsHealingRoll(ctx, def, 'roll_type_is_rolled_at_min') || listsHealingRoll(ctx, def, 'next_roll_type_is_rolled_at_min');
    hints.halve ||= listsHealingRoll(ctx, def, 'roll_type_roll_result_is_halved') || listsHealingRoll(ctx, def, 'next_roll_type_roll_result_is_halved');
    hints.double ||= listsHealingRoll(ctx, def, 'roll_type_roll_result_is_doubled') || listsHealingRoll(ctx, def, 'next_roll_type_roll_result_is_doubled');
  }
  return hints;
}
/** A caster status's definition (the inline def when it carries layers, else the catalog one). */
function casterStatusDefinition(ctx: JanusCtx, status: Row): Row {
  const def = heldHealingStatusDefinition(ctx, status);
  if (!def) definitionNotHeld('healing_roll:caster-status-definition-not-held', status.status_item_id);
  return def!;
}
// ── process_status_effects_next_roll_type_modifiers() (via resolve_next_roll_type_dice_arr) — IN THE BROWSER (2026-10-07 follow-up): every
// status the healer holds NOW (the rows it held + the ones this use applied — a dispatched Maximum Mend's own status) ORs its roll-type
// extremes / halve / double for the Healing Roll into this target's roll, then every one whose triggers_on_next_roll_type lists it is USED UP
// by this roll (remove_status_effect_status_id_single_connection: gone from the in-memory row, so a later target of the same heal no longer
// sees it — Soothing Wind doubles the first target only). A status this use just applied is never persisted (PHP inserts then deletes it).
function healingStatusModifiers(ctx: JanusCtx, caster: Row): HealHints {
  const hints: HealHints = { max: false, min: false, halve: false, double: false };
  const usedUp: Row[] = [];
  for (const status of activeStatuses(ctx, caster)) {
    const def = casterStatusDefinition(ctx, status);
    hints.max ||= listsHealingRoll(ctx, def, 'roll_type_is_rolled_at_max') || listsHealingRoll(ctx, def, 'next_roll_type_is_rolled_at_max');
    hints.min ||= listsHealingRoll(ctx, def, 'roll_type_is_rolled_at_min') || listsHealingRoll(ctx, def, 'next_roll_type_is_rolled_at_min');
    hints.halve ||= listsHealingRoll(ctx, def, 'roll_type_roll_result_is_halved') || listsHealingRoll(ctx, def, 'next_roll_type_roll_result_is_halved');
    hints.double ||= listsHealingRoll(ctx, def, 'roll_type_roll_result_is_doubled') || listsHealingRoll(ctx, def, 'next_roll_type_roll_result_is_doubled');
    if (listsHealingRoll(ctx, def, 'triggers_on_next_roll_type')) usedUp.push(status);
  }
  for (const status of usedUp) if (activeStatuses(ctx, caster).includes(status)) removeStoredStatus(ctx, caster, status, 'single');
  return hints;
}
/** Settle held definitions and any next-roll removal before drawing. */
function healingStatusPreDraw(ctx: JanusCtx, caster: Row, against = false): void {
  for (const status of against ? healingPayloadStatuses(ctx, caster) : activeStatuses(ctx, caster)) {
    const def = against ? recipientStatusDefinition(ctx, status) : casterStatusDefinition(ctx, status);
    const nextKeys = against ? ['next_healing_roll_against_adv', 'next_healing_roll_against_dis'] : ['next_healing_roll_adv', 'next_healing_roll_dis'];
    if (!(nextKeys.some(key => ctx.read.has(def, key)) || (!against && listsHealingRoll(ctx, def, 'triggers_on_next_roll_type')))) continue;
    if (storedStatusHasSpecialTeardown(ctx, status))
      handBack('healing_roll:unmodelled:next-roll-status-removal-special');
    if (carrierHasTrigger(ctx, caster, 'triggers_when_caster_loses_status_effect', 'healing_roll:next-roll-status-lose-trigger', upper(status.status_item_id)))
      handBack('healing_roll:unmodelled:next-roll-status-lose-trigger');
  }
}
/** process_status_effects_next_adv_dis: read each held row once, then retire its single connection before the CMRT fire. */
function healingNextAdvDis(ctx: JanusCtx, holder: Row, against = false): number {
  let delta = 0;
  const keys = against ? ['next_healing_roll_against_adv', 'next_healing_roll_against_dis'] : ['next_healing_roll_adv', 'next_healing_roll_dis'];
  for (const status of against ? healingPayloadStatuses(ctx, holder) : activeStatuses(ctx, holder)) {
    const def = against ? recipientStatusDefinition(ctx, status) : casterStatusDefinition(ctx, status);
    const adv = ctx.read.has(def, keys[0]!), dis = ctx.read.has(def, keys[1]!);
    delta += Number(adv) - Number(dis);
    if ((adv || dis) && activeStatuses(ctx, holder).some(held => held === status
      || (status.status_id != null && upper(held.status_id) === upper(status.status_id)))) removeStoredStatus(ctx, holder, status, 'single');
  }
  return delta;
}
function heldHealingStatusDefinition(ctx: JanusCtx, status: Row): Row | null {
  return fullDefinition(record(status.status_arr), 'any') ?? fullDefinition(ctx.read.definition(statusItemId(status)), 'any');
}
/** The hints the hint-only carriers that fire against `target` merge (janus_cmrt_merge_post_effect_hints_from_ability_row). The FIRST target
 * that lets one fire dispatches through the executor. Every later target only re-judges these (hints, no row). */
function healingCmrtHints(ctx: JanusCtx, caster: Row, target: Row, carriers: TriggerCandidate[]): HealHints {
  const hints: HealHints = { max: false, min: false, halve: false, double: false };
  if (!carriers.length) return hints;
  const read = janusCtxTriggerReaders(ctx);
  const event = { trigger: CMRT_TRIGGER, rollType: HEALING_ROLL };
  for (const candidate of carriers) {
    if (!triggerCall(() => gateTriggerCandidate(read, candidate, caster, event, target)).fire) continue;
    const def = candidate.ability;
    hints.max ||= listsHealingRoll(ctx, def, 'roll_type_is_rolled_at_max') || listsHealingRoll(ctx, def, 'next_roll_type_is_rolled_at_max');
    hints.min ||= listsHealingRoll(ctx, def, 'roll_type_is_rolled_at_min') || listsHealingRoll(ctx, def, 'next_roll_type_is_rolled_at_min');
    hints.halve ||= listsHealingRoll(ctx, def, 'roll_type_roll_result_is_halved') || listsHealingRoll(ctx, def, 'next_roll_type_roll_result_is_halved');
    hints.double ||= listsHealingRoll(ctx, def, 'roll_type_roll_result_is_doubled') || listsHealingRoll(ctx, def, 'next_roll_type_roll_result_is_doubled');
  }
  return hints;
}

/** includes/functions/janus/rolls/healing_roll.php:healing_roll */
export function healingRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  const eligible = effectTargets(ctx, input, ability, caster, targets);
  if (!eligible) return;
  if (ctx.read.has(ability, 'healing_dice_quantity_mod'))
    handBack('healing_roll:authored-dice-modifiers');
  for (const key of ['healing_rolls_adv', 'healing_rolls_dis', 'healing_roll_at_max', 'healing_roll_at_min',
    'roll_type_adv', 'roll_type_dis', 'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min',
    'extra_healing_roll', 'healing_bonus', 'healing_roll_mod'])
    if (ctx.read.has(caster, key)) handBack(`healing_roll:caster:${key}`);
  const compiledPassives = record(record(record(caster.computed)?.creature)?.abilities)?.passive_ability;
  if (compiledPassives != null && !Array.isArray(compiledPassives)) handBack('healing_roll:passive-carriers-not-held');
  extraHealingPools(ctx, input, caster); // validate held sources before triggers or any primary draw
  healingRerolls(ctx, caster);
  const rawDice = ctx.read.value(ability, 'dice');
  const rawDie = ctx.read.value(ability, 'die');
  if (!/^\d+$/.test(String(rawDice)) || !/^\d+$/.test(String(rawDie)))
    handBack('healing_roll:unmodelled:variable-dice');
  let dice = requiredInt(rawDice, 'healing_roll:invalid-dice');
  const die = requiredInt(rawDie, 'healing_roll:invalid-die');
  if (dice < 0 || dice > 100 || (dice > 0 && die <= 0)) handBack('healing_roll:dice-shape');
  const rawMod = ctx.read.value(ability, 'mod');
  const mod = healingVariable(ctx, input, caster, rawMod);
  // upcast_roll() (healing_roll.php ~L102): a level-cast spell's dice_upcast (upcast.ts); a mod the upcast grows → PHP (no face yet).
  const up = upcastRoll(ctx, ability, dice, mod);
  if (up.mod !== mod) handBack('healing_roll:upcast-mod-face');
  dice = up.dice;
  if (dice > 100) handBack('healing_roll:dice-shape');
  // The roll type the CMRT fire and the next-roll modifiers key on (healing_roll.php: layer_value($item_id_used_arr, 'roll_type')): an
  // empty one fires nothing and reads no modifier (PHP's uuid guards); any roll type other than Healing Roll is not modelled.
  const rollType = upper(ctx.read.value(ability, 'roll_type'));
  if (rollType !== '' && rollType !== HEALING_ROLL) handBack('healing_roll:unmodelled:roll-type');
  // Settled on state before any draw: the healer's CMRT carriers (hint-only passives per target, every other carrier through the executor),
  // and the caster STATUSES process_status_effects_next_roll_type_modifiers reads (max / min / halve / double; a next-roll carrier used up).
  const cmrtCarriers = healingCmrtCarriers(ctx, caster, rollType);
  const cmrtFrame = { fired: false };
  if (rollType !== '') healingStatusPreDraw(ctx, caster);
  for (const target of eligible) {
    // The damage path honors this per-target gate. A nullified heal must neither draw nor remove Burning.
    const postGate = nullifyPostEffectConditions(ctx, ability, caster, target);
    if (!postGate) continue;
    // This recipient's status definitions (and a no-heal status's nested ones) before its draw: the page fetches them by id.
    settleRecipientStatuses(ctx, target);
    healingStatusPreDraw(ctx, target, true);
    const nextDelta = healingNextAdvDis(ctx, caster) + healingNextAdvDis(ctx, target, true);
    // Status cleanup is unconditional on the amount restored. Prone has no healing branch.
    const statuses = ctx.read.statuses(target);
    if (statuses.some(status => statusItemId(status) === CRIPPLED) && !ctx.read.definition(PRONE))
      definitionNotHeld('healing_roll:recovery-status-definition-not-held', PRONE);
    // fire_caster_makes_roll_type_trigger (+ the hint-only re-judge), then resolve_next_roll_type_dice_arr: the dispatched carriers' hints
    // seed the roll, the healer's statuses OR theirs in (and a next-roll status is used up); a max / min forces every die to that face (no
    // draw) and appends the marker; halve / double apply after the modifier.
    // HEALING ROLL ADV / DIS (2026-10-08, Thief Corpsman "Efficient Healer": a passive's roll_type_adv [Healing Roll] — PHP draws 8d10 keep 4):
    // janus_roll_type_adv_dis_item_delta (the ability's own roll_type_adv / _dis) + janus_roll_type_adv_dis_entities_only_delta (the healer's
    // compiled roll-type sources — passives, feats, consented reactions and statuses — and the target's against axes),
    // the entities delta taken before AND after the CMRT fire (the larger counts), clamped to ±5.
    const entitiesDelta = (): number => rollType === '' ? 0
      : rollTypeModifierStacks(ctx, caster, [rollType], ['adv', 'dis'], { usedItemType: '', usedRollType: rollType, rollTo: ecId(target), opposing: target,
        reactionAllows: def => {
          const id = upper(itemField(def, 'item_id'));
          // A reaction's lingering item-as-status is the same source, including out of combat where its reaction remains available.
          return !activeStatuses(ctx, caster).some(status => statusItemId(status) === id)
            && consentAllows(ctx, caster, def, id, CMRT_TRIGGER, 'reaction_ability');
        } })
        + healingAgainstStacks(ctx, target, caster, rollType);
    const itemDelta = rollType === '' ? 0 : (listsHealingRoll(ctx, ability, 'roll_type_adv') ? 1 : 0) - (listsHealingRoll(ctx, ability, 'roll_type_dis') ? 1 : 0);
    const entitiesPre = entitiesDelta();
    const hints = cmrtFrame.fired ? healingCmrtHints(ctx, caster, target, cmrtCarriers.hintOnly)
      : !cmrtCarriers.dispatch ? { max: false, min: false, halve: false, double: false }
      : healingCmrtExecutorFire(ctx, caster, target, cmrtFrame);
    const authoredDelta = ctx.read.entries(ability, 'healing_roll_adv_dis_stacks')
      .reduce<number>((sum, entry) => sum + requiredInt(entry, 'healing_roll:invalid-adv-dis-stacks'), 0);
    const advDisStacks = Math.max(-5, Math.min(5, nextDelta + authoredDelta + itemDelta + Math.max(entitiesPre, entitiesDelta())));
    if (rollType !== '') {
      const held = healingStatusModifiers(ctx, caster);
      hints.max ||= held.max; hints.min ||= held.min; hints.halve ||= held.halve; hints.double ||= held.double;
      const against = healingAgainstModifiers(ctx, caster, target);
      hints.max ||= against.max; hints.min ||= against.min; hints.halve ||= against.halve; hints.double ||= against.double;
    }
    // heal_target_arr reads the target AFTER the CMRT fire (a dispatched effect may have changed it) — read here, still before the draw
    const vitality = record(target.computed)?.vitality;
    const computedVitality = record(vitality);
    if (computedVitality?.v_current == null || computedVitality?.v_max == null ||
      requiredInt(ecLean(target, 'v_current'), 'heal_target_arr:vitality-projection-mismatch') !== requiredInt(computedVitality.v_current, 'heal_target_arr:vitality-projection-mismatch') ||
      requiredInt(ecLean(target, 'encounter_creature_dead') ?? ctx.read.deadState(target), 'healing_roll:dead-state-not-held'))
      handBack('heal_target_arr:vitality-or-dead');
    // Read again after CMRT/next-roll consumption: effects applied or removed by this target's trigger are current.
    const extras = extraHealingPools(ctx, input, caster).map(pool => ({ ...pool, advDisStacks: Math.max(-5, Math.min(5,
      (ctx.read.entries(pool.definition, 'roll_type_adv').some(e => upper(layerListEntryUuid(e)) === EXTRA_HEALING_ROLL) ? 1 : 0)
      - (ctx.read.entries(pool.definition, 'roll_type_dis').some(e => upper(layerListEntryUuid(e)) === EXTRA_HEALING_ROLL) ? 1 : 0)
      + rollTypeModifierStacks(ctx, caster, [EXTRA_HEALING_ROLL], ['adv', 'dis'],
        { usedItemType: '', usedRollType: EXTRA_HEALING_ROLL, rollTo: ecId(target), opposing: target })
      + healingAgainstStacks(ctx, target, caster, EXTRA_HEALING_ROLL))) }));
    healingReceiverTriggers(ctx, target, caster, true);
    const reroll = healingRerolls(ctx, caster); // current providers after CMRT and next-roll consumption
    const forced = (hints.max || hints.min) && dice > 0 && die > 0 ? (hints.max ? die : 1) : null;
    const rolled: { total: number; faces: DieTuple[] } = forced === null ? rollDiceFaces(ctx, dice, die, advDisStacks)
      : { total: dice * forced, faces: Array.from({ length: dice }, (_, i) => [forced, `d${die}_${i + 1} ${hints.max ? 'Ω' : 'α'}`]) };
    if (reroll) rerollHealingOnesAndTwos(ctx, rolled, die);
    let result = rolled.total + mod;
    const fullFaces: unknown[] = [...rolled.faces];
    if (hints.max) fullFaces.push(['Ω', 'Roll Type is Rolled At Max']);
    else if (hints.min) fullFaces.push(['α', 'Roll Type is Rolled At Min']);
    for (const pool of extras) {
      const extra = rollDiceFaces(ctx, pool.dice, pool.die, pool.advDisStacks).total + pool.mod;
      result += extra;
      fullFaces.push([extra, `Extra Healing Roll | ${itemField(pool.definition, 'item_name') ?? ''} | ${pool.dice}d${pool.die}${pool.mod ? ` + ${pool.mod}` : ''}`]);
    }
    if (mod) fullFaces.push([String(mod), 'Healing Mod']);
    if (!fullFaces.length) fullFaces.push([mod, 'roll_result (modifier only)']);
    // Max/min affects the primary pool only; halve/double covers the primary modifier and every extra pool.
    if (hints.halve) { result = Math.floor(result * 0.5); fullFaces.push(['ξ', 'Roll Type Roll Result is Halved']); }
    if (hints.double) { result *= 2; fullFaces.push(['Γ', 'Roll Type Roll Result is Doubled']); }
    // ft_nullify_heal_roll_if_blocked: a no-heal status (Withering) strikes the total to 0 and names itself in the log.
    const blockedBy = healingBlockedBy(ctx, target);
    if (blockedBy) { result = 0; fullFaces.push(['∅', `Healing Nullified · ${blockedBy}`]); }
    // post_effect_conditions.php runs before heal_target_arr and the heal log insert.
    if (!postEffectConditions(ctx, input, ability, caster, target)) continue;
    // Re-read after post-effect conditions, then apply the shared amount/cap/block/cleanup contract.
    healVitalityAmount(ctx, input, ability, target, result);
    insertRoll(ctx, { roll_type_1: HEALING_ROLL, roll_type_2: '', roll_item_id: input.abilityId,
      by: caster, to: target, roll_result: result, rolls_full: fullFaces, dice, die, mod, adv_dis_stacks: advDisStacks });
    healingReceiverTriggers(ctx, target, caster);
  }
}
